import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, Building, Settings, MapPin, Headphones, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getText, getCurrentLanguage } from '../../services/dataSync';

const LeadershipTeamPage = () => {
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());

  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  const leadershipTeam = [
    {
      name: getText('about.structure.leadership.president.name'),
      position: getText('about.structure.leadership.president.position'),
      department: getText('about.structure.leadership.president.department'),
      image: '/images/4.jpg'
    },
    {
      name: getText('about.structure.leadership.vicePresident.name'),
      position: getText('about.structure.leadership.vicePresident.position'),
      department: getText('about.structure.leadership.vicePresident.department'),
      image: '/images/2.jpg'
    },
    {
      name: getText('about.structure.leadership.secretary.name'),
      position: getText('about.structure.leadership.secretary.position'),
      department: getText('about.structure.leadership.secretary.department'),
      image: '/images/deepa.jpeg'
    },
    {
      name: getText('about.structure.leadership.treasurer.name'),
      position: getText('about.structure.leadership.treasurer.position'),
      department: getText('about.structure.leadership.treasurer.department'),
      image: '/images/napte.jpeg'
    },
    {
      name: getText('about.structure.leadership.chiefConsultant.name'),
      position: getText('about.structure.leadership.chiefConsultant.position'),
      department: getText('about.structure.leadership.chiefConsultant.department'),
      image: '/images/mundada.jpeg'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden pt-6">
        <div className="container relative z-10 py-8 md:py-12">
          <div className="max-w-5xl mx-auto">
            <div className="mb-4">
              <Link to="/about" className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-800 hover:text-black border-2 border-primary-600 hover:border-primary-700 rounded-lg transition-all duration-300 group shadow-md hover:shadow-lg">
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium text-sm">{getText('nav.about.submenu.about')}</span>
              </Link>
            </div>

            <div className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight">
                {getText('nav.about.submenu.leadershipTeam') || getText('about.structure.leadership.title') || 'Leadership Team'}
              </h1>
              <p className="text-lg md:text-xl text-blue-100 font-medium">
                {getText('about.structure.leadership.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="section py-16 bg-white">
        <div className="container-medium">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {leadershipTeam.map((leader, index) => (
              <div key={index} className="group">
                <div className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-accent">
                  {leader.image && (
                    <div className="mb-4">
                      <div className="relative w-full max-w-[160px] mx-auto">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-200 to-primary-300 rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
                        <div className="relative bg-gradient-to-br from-white to-gray-50 p-2 rounded-xl shadow-lg transform group-hover:scale-105 transition-transform duration-300">
                          <img
                            src={leader.image}
                            alt={leader.name}
                            className="w-full rounded-lg object-cover shadow-md border-2 border-white"
                            style={{ aspectRatio: '3/4', maxHeight: '200px' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.parentElement.parentElement.style.display = 'none';
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-gray-800 mb-1">{leader.name}</h3>
                  <div className="w-12 h-0.5 bg-gradient-to-r from-primary-500 to-primary-600 mx-auto mb-2 rounded-full"></div>
                  <p className="text-primary font-semibold mb-2">{leader.position}</p>
                  <p className="text-sm text-gray-600">{leader.department}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Link to Committee */}
          <div className="mt-12 text-center">
            <Link
              to="/about/committee"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              {getText('about.leadership.viewCommittee') || 'View Committee Members'}
              <ArrowLeft className="h-4 w-4 rotate-180" />
            </Link>
          </div>
        </div>
      </section>

      {/* Organizational Chart */}
      <section className="section py-16 bg-gradient-to-r from-gray-50 to-blue-50">
        <div className="container-medium">
          <div className="section-header mb-12">
            <h2 className="section-title">{getText('about.structure.chart.title')}</h2>
            <p className="text-lg text-gray max-w-2xl mx-auto">
              {getText('about.structure.chart.desc')}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex flex-col items-center space-y-8">
              <div className="bg-orange-600 rounded-2xl p-6 text-center text-white shadow-lg">
                <Building className="h-8 w-8 mx-auto mb-2" />
                <h3 className="text-lg font-bold">{getText('about.structure.leadership.president.name')}</h3>
                <p className="text-blue-100">{getText('about.structure.leadership.president.position')}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-4 text-center text-white shadow-lg">
                  <User className="h-6 w-6 mx-auto mb-2" />
                  <h4 className="font-semibold">{getText('about.structure.leadership.vicePresident.name')}</h4>
                  <p className="text-blue-100 text-sm">{getText('about.structure.leadership.vicePresident.position')}</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl p-4 text-center text-white shadow-lg">
                  <User className="h-6 w-6 mx-auto mb-2" />
                  <h4 className="font-semibold">{getText('about.structure.leadership.secretary.name')}</h4>
                  <p className="text-emerald-100 text-sm">{getText('about.structure.leadership.secretary.position')}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl p-4 text-center text-white shadow-lg">
                  <User className="h-6 w-6 mx-auto mb-2" />
                  <h4 className="font-semibold">{getText('about.structure.leadership.treasurer.name')}</h4>
                  <p className="text-purple-100 text-sm">{getText('about.structure.leadership.treasurer.position')}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-500 to-amber-700 rounded-xl p-4 text-center text-white shadow-lg">
                  <User className="h-6 w-6 mx-auto mb-2" />
                  <h4 className="font-semibold">{getText('about.structure.leadership.chiefConsultant.name')}</h4>
                  <p className="text-amber-100 text-sm">{getText('about.structure.leadership.chiefConsultant.position')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center shadow-lg">
                  <Settings className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                  <h4 className="font-semibold text-gray-800">{getText('about.structure.chart.technical.title')}</h4>
                  <p className="text-gray-600 text-sm">{getText('about.structure.chart.technical.subtitle')}</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 text-center shadow-lg">
                  <MapPin className="h-6 w-6 mx-auto mb-2 text-emerald-600" />
                  <h4 className="font-semibold text-gray-800">{getText('about.structure.chart.regional.title')}</h4>
                  <p className="text-gray-600 text-sm">{getText('about.structure.chart.regional.subtitle')}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center shadow-lg">
                  <Headphones className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                  <h4 className="font-semibold text-gray-800">{getText('about.structure.chart.support.title')}</h4>
                  <p className="text-gray-600 text-sm">{getText('about.structure.chart.support.subtitle')}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 text-center shadow-lg">
                  <Globe className="h-6 w-6 mx-auto mb-2 text-amber-600" />
                  <h4 className="font-semibold text-gray-800">{getText('about.structure.chart.external.title')}</h4>
                  <p className="text-gray-600 text-sm">{getText('about.structure.chart.external.subtitle')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeadershipTeamPage;
